import { apiClient } from '@/lib/api/client'
import type { PurchaseOrderFormData } from '@/lib/validations/clientPurchaseOrder'

const BASE_PATH = '/organization/client-purchase-orders'

export interface ClientPurchaseOrderClient {
  id: number
  name: string
  code: string
}

export interface ClientPurchaseOrderRecord {
  id: number
  client_id: number
  po_number: string
  po_date?: string | null
  domain?: string | null
  description?: string | null
  portfolio?: string | null
  sales_person?: string | null
  amount?: number | null
  duration?: string | null
  start_date?: string | null
  end_date?: string | null
  is_active?: boolean
  is_expiring_soon?: boolean
  days_until_expiry?: number | null
  client?: ClientPurchaseOrderClient | null
  created_at?: string | null
  updated_at?: string | null
}

export interface PurchaseOrderImportResult {
  imported: number
  updated: number
  errors: string[]
}

function normalizePayload(payload: PurchaseOrderFormData) {
  return {
    client_id: payload.client_id,
    po_number: payload.po_number.trim(),
    po_date: payload.po_date || null,
    domain: payload.domain?.trim() || null,
    description: payload.description?.trim() || null,
    portfolio: payload.portfolio?.trim() || null,
    sales_person: payload.sales_person?.trim() || null,
    amount: payload.amount ?? null,
    duration: payload.duration?.trim() || null,
    start_date: payload.start_date || null,
    end_date: payload.end_date || null,
  }
}

function unwrapRecord(payload: unknown): ClientPurchaseOrderRecord {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: ClientPurchaseOrderRecord }).data
  }
  return payload as ClientPurchaseOrderRecord
}

export async function fetchPurchaseOrder(id: number): Promise<ClientPurchaseOrderRecord> {
  const { data } = await apiClient.get(`${BASE_PATH}/${id}`)
  return unwrapRecord(data)
}

export async function createPurchaseOrder(payload: PurchaseOrderFormData) {
  const { data } = await apiClient.post(BASE_PATH, normalizePayload(payload))
  return data
}

export async function updatePurchaseOrder(id: number, payload: PurchaseOrderFormData) {
  const { data } = await apiClient.put(`${BASE_PATH}/${id}`, normalizePayload(payload))
  return data
}

export async function importPurchaseOrders(file: File): Promise<PurchaseOrderImportResult> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await apiClient.post<{ data: PurchaseOrderImportResult }>(
    `${BASE_PATH}/import`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return data?.data ?? { imported: 0, updated: 0, errors: [] }
}
